import React from "react";
import moment from "moment";
import {
  formatStatusText,
  formatStatusBadgeClass,
  calculateAge,
  extractDisplayString,
} from "./nocUtils";

interface NocEmployeeCardProps {
  nocData: any;
  nocType: string;
  editableDepartment?: string;
  onDepartmentChange?: (value: string) => void;
  editableContactNumber?: string;
  onContactNumberChange?: (value: string) => void;
  contactNumberError?: string;
}

export const NocEmployeeCard: React.FC<NocEmployeeCardProps> = ({
  nocData,
  nocType,
  editableDepartment,
  onDepartmentChange,
  editableContactNumber,
  onContactNumberChange,
  contactNumberError,
}) => {
  const isDeptEditable =
    nocData?.enabled_fields_fields_json?.Department === "X" ||
    nocData?.enabled_fields_json?.Department === "X";

  const isContactEditable =
    nocData?.enabled_fields_fields_json?.ContactNumber === "X" ||
    nocData?.enabled_fields_json?.ContactNumber === "X" ||
    nocData?.enabled_fields_fields_json?.Phone === "X" ||
    nocData?.enabled_fields_json?.Phone === "X" ||
    nocData?.enabled_fields_fields_json?.contactNumber === "X" ||
    nocData?.enabled_fields_json?.contactNumber === "X";

  const rawDepartment =
    editableDepartment !== undefined
      ? editableDepartment
      : nocData?.department ||
      nocData?.dept ||
      nocData?.departmentName ||
      nocData?.employee?.details?.sap_department_designation_wings?.department_name ||
      nocData?.employee?.sap_department_designation_wings?.department_name ||
      nocData?.employee?.details?.department_name ||
      nocData?.employee?.department_details?.departmentName ||
      nocData?.employee?.departmentName ||
      nocData?.employee?.department ||
      "N/A";
  const resolvedDepartment = extractDisplayString(rawDepartment, "N/A");

  const empName = nocData?.employee
    ? `${nocData.employee.firstname || ""} ${nocData.employee.lsastname || nocData.employee.lastname || ""}`.trim()
    : extractDisplayString(nocData?.employeeName, "N/A");

  const designation = extractDisplayString(
    nocData?.employee?.details?.sap_department_designation_wings?.designation_name ||
    nocData?.employee?.sap_department_designation_wings?.designation_name ||
    nocData?.employee?.designation ||
    nocData?.designation,
    "N/A"
  );

  const doj = nocData?.employee?.date_of_joining
    ? moment.utc(nocData.employee.date_of_joining).format("DD-MM-YYYY")
    : "N/A";

  const employmentType = extractDisplayString(
    nocData?.employee?.employmentType ||
    nocData?.employee?.details?.employmentType ||
    (nocData as any)?.employmentType ||
    nocData?.employee?.employmentStatus,
    "N/A"
  );

  const rawPhone =
    editableContactNumber !== undefined
      ? editableContactNumber
      : nocData?.contactNumber ||
      nocData?.employee?.phone ||
      nocData?.employee?.details?.phone ||
      nocData?.contact_info ||
      "";
  const phone = extractDisplayString(rawPhone, "N/A");
  const email = extractDisplayString(
    nocData?.email || nocData?.employee?.email || nocData?.emailId,
    "N/A"
  );
  const payScale = extractDisplayString(
    nocData?.employee?.pay_scale || nocData?.pay_scale,
    ""
  );

  const fatherName = extractDisplayString(
    nocData?.fatherName || nocData?.employee?.details?.father_name,
    ""
  );

  const resolvedAge = extractDisplayString(
    nocData?.age ||
    nocData?.employee?.age ||
    nocData?.employee?.details?.age ||
    calculateAge(nocData?.employee?.date_of_birth || nocData?.dateOfBirth),
    ""
  );

  const resolvedCategory = extractDisplayString(
    nocData?.category ||
    nocData?.employee?.details?.category ||
    nocData?.employee?.category ||
    nocData?.details?.category,
    ""
  );

  const presentAddress = extractDisplayString(nocData?.presentAddress, "");
  const permanentAddress = extractDisplayString(nocData?.permanentAddress, "");

  return (
    <>
      {/* Application Info Card */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="ti ti-file-certificate me-2 text-primary"></i>
            Application Info
          </h5>
          <span className={formatStatusBadgeClass(nocData?.requestStatus)}>
            {formatStatusText(nocData?.requestStatus)}
          </span>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {nocType === "NOC_ONLINECOURSE" && (
              <div className="col-md-6 col-sm-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block text-uppercase fw-medium mb-1">Employee</small>
                  <div className="fw-bold text-dark fs-15 font-monospace">
                    {nocData?.empId || nocData?.empID || nocData?.employee?.empId || (nocData as any)?.employee?.id || "-"}
                  </div>
                </div>
              </div>
            )}

            <div
              className={
                nocType === "NOC_ONLINECOURSE" ||
                  nocType === "NOC_ONLINECOURSE-REIMB" ||
                  nocData?.referenceNumber1 ||
                  nocData?.online_course?.referenceNumber
                  ? "col-md-6 col-sm-6"
                  : "col-md-6 col-sm-12"
              }
            >
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block text-uppercase fw-medium mb-1">Reference No</small>
                <div className="fw-bold text-dark fs-15 font-monospace">
                  {nocData?.referenceNumber || "-"}
                </div>
              </div>
            </div>

            {(nocType === "NOC_ONLINECOURSE-REIMB" ||
              nocData?.referenceNumber1 ||
              nocData?.online_course?.referenceNumber) && (
                <div className="col-md-6 col-sm-6">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block text-uppercase fw-medium mb-1">
                      Associated Reference No
                    </small>
                    <div className="fw-bold text-dark fs-15 font-monospace">
                      {nocData?.referenceNumber1 || nocData?.online_course?.referenceNumber || "-"}
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Employee Details Card */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-header bg-light">
          <h5 className="card-title mb-0">
            <i className="ti ti-user me-2 text-primary"></i>
            Employee Details
          </h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4 col-sm-6">
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block uppercase fw-medium mb-1">Name</small>
                <div className="fw-bold text-dark fs-15">{empName}</div>
              </div>
            </div>

            {/* <div className="col-md-4 col-sm-6">
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block uppercase fw-medium mb-1">Employee ID</small>
                <div className="fw-bold text-dark fs-15 font-monospace">
                  {nocData?.empID || nocData?.empId || nocData?.employee?.empId || (nocData as any)?.employee?.id || "-"}
                </div>
              </div>
            </div> */}

            <div className="col-md-4 col-sm-6">
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block uppercase fw-medium mb-1">Designation</small>
                <div className="fw-bold text-dark fs-15">{designation}</div>
              </div>
            </div>

            <div className="col-md-4 col-sm-6">
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block uppercase fw-medium mb-1">
                  Date of Joining in DMRC
                </small>
                <div className="fw-bold text-dark fs-15">{doj}</div>
              </div>
            </div>

            <div className="col-md-4 col-sm-6">
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block uppercase fw-medium mb-1">Employment Status</small>
                <div className="fw-bold text-dark fs-15">{employmentType}</div>
              </div>
            </div>

            <div className="col-md-4 col-sm-6">
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block uppercase fw-medium mb-1">
                  Active Phone Number
                  {isContactEditable && <span className="text-danger ms-1">*</span>}
                </small>
                {isContactEditable ? (
                  <div>
                    <input
                      type="tel"
                      className={`form-control mt-1 ${contactNumberError ? "is-invalid" : ""}`}
                      value={editableContactNumber !== undefined ? editableContactNumber : (rawPhone === "N/A" ? "" : rawPhone)}
                      maxLength={10}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        onContactNumberChange?.(val);
                      }}
                      placeholder="Enter 10-digit mobile number"
                    />
                    {contactNumberError && (
                      <div className="text-danger small mt-1">{contactNumberError}</div>
                    )}
                  </div>
                ) : (
                  <div className="fw-bold text-dark fs-15">{phone}</div>
                )}
              </div>
            </div>

            <div className="col-md-4 col-sm-6">
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block uppercase fw-medium mb-1">Email ID</small>
                <div className="fw-bold text-dark fs-15">{email}</div>
              </div>
            </div>

            <div className="col-md-4 col-sm-6">
              <div className="p-3 bg-light rounded border h-100">
                <small className="text-muted d-block uppercase fw-medium mb-1">Department</small>
                {isDeptEditable ? (
                  <input
                    type="text"
                    className="form-control mt-1"
                    value={resolvedDepartment}
                    onChange={(e) => onDepartmentChange?.(e.target.value)}
                  />
                ) : (
                  <div className="fw-bold text-dark fs-15">{resolvedDepartment}</div>
                )}
              </div>
            </div>

            {/* {payScale && (
              <div className="col-md-4 col-sm-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Pay Scale</small>
                  <div className="fw-bold text-dark fs-15">{payScale}</div>
                </div>
              </div>
            )} */}

            {fatherName && (
              <div className="col-md-4 col-sm-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Father's Name</small>
                  <div className="fw-bold text-dark fs-15">{fatherName}</div>
                </div>
              </div>
            )}

            {resolvedAge && (
              <div className="col-md-4 col-sm-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Age</small>
                  <div className="fw-bold text-dark fs-15">{resolvedAge}</div>
                </div>
              </div>
            )}

            {resolvedCategory && nocType !== "NOC_ONLINECOURSE" && nocType !== "NOC_ONLINECOURSE-REIMB" && (
              <div className="col-md-4 col-sm-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Category</small>
                  <div className="fw-bold text-dark fs-15">{resolvedCategory}</div>
                </div>
              </div>
            )}

            {presentAddress && (
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Present Address</small>
                  <div className="fw-medium text-dark">{presentAddress}</div>
                </div>
              </div>
            )}

            {permanentAddress && (
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Permanent Address</small>
                  <div className="fw-medium text-dark">{permanentAddress}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
